import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Directions } from "@mui/icons-material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Handle, Node, NodeProps, Position, useUpdateNodeInternals } from "@xyflow/react";
import { useDebouncedCallback } from "use-debounce";

import { buildColumnDefs, gridOptions } from "fond/topologicalView/spliceRangeColumns";
import SpliceRangeNodeSkeleton from "fond/topologicalView/SpliceRangeNodeSkeleton";
import { useTopologicalViewContext } from "fond/topologicalView/TopologicalViewContext";
import { TopologicalNodeData } from "fond/topologicalView/types";
import { AgGrid } from "fond/widgets";

const SpliceRangeNode: React.FC<Pick<NodeProps<Node<TopologicalNodeData>>, "data">> = ({ data }) => {
  const hasLeafRow = useMemo(() => data.spliceRecords.some((row) => row.leaf !== null), [data]);
  const { ref, inView } = useInView();
  const [visible, setVisible] = useState<boolean>(false);
  const slowlySetVisible = useDebouncedCallback((value) => setVisible(value), 15_000);
  const currentlyVisible = inView || visible;
  const { selectFeature } = useTopologicalViewContext();
  const updateNodeInternals = useUpdateNodeInternals();
  const onGridReady = useCallback(() => updateNodeInternals(data.id), [updateNodeInternals, data.id]);

  // If the node is in view, show it immediately. If it goes out of view, wait a while before hiding it again.
  useEffect(() => {
    if (inView) {
      slowlySetVisible.cancel();
      setVisible(true);
    } else {
      slowlySetVisible(false);
    }
  }, [inView, setVisible, slowlySetVisible]);

  const onShowFeatureProperties = useCallback(() => {
    selectFeature(
      { id: data.id },
      {
        highlightFeature: true,
        showProperties: true,
        zoomMap: true,
      }
    );
  }, [data.id, selectFeature]);

  const colDefs = useMemo(() => buildColumnDefs({ hasLeafRow, columnWidths: data.columnWidths }), [hasLeafRow, data.columnWidths]);

  return (
    <div
      ref={ref}
      style={{
        width: data.dimensions.width,
        maxHeight: 800,
        textAlign: "center",
        backgroundColor: "white",
        border: "1px solid #bbb",
        borderRadius: 4,
      }}
    >
      <Stack justifyContent="center" alignItems="center" direction="row" sx={{ py: 1, borderBottom: "1px solid #bbb" }}>
        <Typography>{data.label}</Typography>
        {data.id && (
          <Tooltip title="View feature" arrow>
            <IconButton size="small" color="primary" onClick={onShowFeatureProperties}>
              <Directions style={{ fontSize: "18px" }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {data.spliceRecords.length === 0 && <Handle type="source" position={Position.Right} />}
      {data.spliceRecords.length > 0 && currentlyVisible && (
        <Box
          data-testid="splice-node-grid"
          sx={{
            ".ag-theme-alpine .ag-root-wrapper": {
              border: "none !important",
            },
          }}
        >
          <AgGrid columnDefs={colDefs} rowData={data.spliceRecords} gridOptions={gridOptions} className="nodrag" onGridReady={onGridReady} />
        </Box>
      )}
      {data.spliceRecords.length > 0 && !currentlyVisible && (
        <SpliceRangeNodeSkeleton spliceRecords={data.spliceRecords} dimensions={data.dimensions} />
      )}
    </div>
  );
};

export default SpliceRangeNode;
